import * as PIXI from "pixi.js";
import data from "../configs/data";
import {Button} from "../controlElements/Button";
import {CheckBox} from "../controlElements/CheckBox";
import {PixiApplication} from "../PixiApplication";
import {BalancePanel} from "./BalancePanel";
import {BetPanel} from "./BetPanel";
import {CurrencySwitcher} from "./CurrencySwitcher";
import {ICurrencySwitcher} from "./ICurrencySwitcher";
import {IToolbarMenu} from "./IToolbarMenu";
import {ToolBarMenu} from "./ToolBarMenu";
import {WinField} from "./WinField";

export class GamePanel extends PIXI.Container {

    public static getInstance(): GamePanel {
        if (!GamePanel.OBJ_INSTANCE) {
            GamePanel.OBJ_INSTANCE = new GamePanel();
        }
        return GamePanel.OBJ_INSTANCE;
    }

    private static OBJ_INSTANCE: GamePanel;
    private dealButton: Button;
    private drawButton: Button;
    private collectButton: Button;
    private speedCheckbox: CheckBox;
    private maxBetButton: Button;
    private doubleUpButton: Button;
    private betPanel: BetPanel;
    private currencySwitcher: ICurrencySwitcher;
    private winField: WinField;
    private toolbarMenu: IToolbarMenu;

    private balancePanel: BalancePanel;
    private readonly BET_PANEL_POSITION = new PIXI.Point(309, 48);
    private readonly CURRENCY_SWITCHER_POSITION = new PIXI.Point(134, 48);
    private readonly WIN_FIELD_POSITION = new PIXI.Point(877, 48);
    private readonly BALANCE_PANEL_POSITION = new PIXI.Point(1035, 48);
    private readonly MAX_BET_BUTTON_POSITION = new PIXI.Point(540, 84);
    private readonly DOUBLE_UP_BUTTON_POSITION = new PIXI.Point(740, 84);
    private readonly SPEED_CHECKBOX_POSITION = new PIXI.Point(1195, 86);
    private readonly CENTRAL_BUTTON_POSITION = new PIXI.Point(PixiApplication.WIDTH / 2, 74);
    private readonly TOOLBAR_POSITION = new PIXI.Point(78, 82);

    private constructor() {
        super();

        this.createAndAddBackground();
        this.createAndAddBetPanel();
        this.createAndAddCurrencySwitcher();
        this.createAndAddMaxBetButton();
        this.createAndAddDoubleUpButton();
        this.createAndAddDealButton();
        this.createAndAddDrawButton();
        this.createAndAddCollectButton();
        this.createAndAddWinField();
        this.createAndAddBalancePanel();
        this.createAndAddSpeedCheckbox();
        this.createAndAddToolBar();
        this.showDealButton();
    }

    public updateBalance(steem: number, sbd: number): void {
        this.balancePanel.updateBalance(steem, sbd);
    }

    public addOnDealButtonClickedListener(listener: Function): void {
        this.dealButton.subscribeOnClicked(listener);
    }

    public addOnDrawButtonClickedListener(listener: Function): void {
        this.drawButton.subscribeOnClicked(listener);
    }

    public addOnCollectButtonClickedListener(listener: Function): void {
        this.collectButton.subscribeOnClicked(listener);
    }

    public addOnDoubleUpButtonClickedListener(listener: Function): void {
        this.doubleUpButton.subscribeOnClicked(listener);
    }

    public addOnSpeedChangedListener(listener: Function): void {
        this.speedCheckbox.subscribeOnClicked(listener);
    }

    public addOnBetValueChangedListener(listener: Function): void {
        this.betPanel.addOnValueChangeListener(listener);
        this.maxBetButton.subscribeOnClicked(() => {
            listener(this.betPanel.getBetValue());
        });
    }

    public showDealButton(): void {
        this.dealButton.visible = true;
        this.drawButton.visible = false;
        this.collectButton.visible = false;
    }

    public showDrawButton(): void {
        this.dealButton.visible = false;
        this.drawButton.visible = true;
        this.collectButton.visible = false;
    }

    public showCollectButton(): void {
        this.dealButton.visible = false;
        this.drawButton.visible = false;
        this.collectButton.visible = true;
    }

    public isSpeedIncreased(): boolean {
        return this.speedCheckbox.checked;
    }

    public increaseSpeed(): void {
        this.speedCheckbox.checked = true;
    }

    public decreaseSpeed(): void {
        this.speedCheckbox.checked = false;
    }

    public getBalance(currencyType: string): number {
        return this.balancePanel.getBalance(currencyType);
    }

    public getCurrency(): string {
        return this.currencySwitcher.getCurrency();
    }

    public getBetValue(): number {
        return this.betPanel.getBetValue();
    }

    public enableCentralButton(): void {
        this.dealButton.enabled = true;
        this.drawButton.enabled = true;
        this.collectButton.enabled = true;
    }

    public enableBetPanel(): void {
        this.maxBetButton.enabled = true;
        this.betPanel.enable();
    }

    public disableBetPanel(): void {
        this.maxBetButton.enabled = false;
        this.betPanel.disable();
    }

    public enableMaxBetButton(): void {
        this.maxBetButton.enabled = true;
    }

    public disableMaxBetButton(): void {
        this.maxBetButton.enabled = false;
    }

    public enableCurrencySwitcher(): void {
        this.currencySwitcher.enable();
    }

    public disableCurrencySwitcher(): void {
        this.currencySwitcher.disable();
    }

    public disableCentralButton(): void {
        this.dealButton.enabled = false;
        this.drawButton.enabled = false;
        this.collectButton.enabled = false;
    }

    public enableDoubleUpButton(): void {
        this.doubleUpButton.enabled = true;
    }

    public disableDoubleUpButton(): void {
        this.doubleUpButton.enabled = false;
    }

    public disablePanel(): void {
        this.disableCentralButton();
        this.disableDoubleUpButton();
        this.disableCurrencySwitcher();
        this.disableBetPanel();
    }

    public updateWinValue(amount: number): void {
        this.winField.updateWinValue(amount);
    }

    public getWinValue(): number {
        return this.winField.getWinValue();
    }

    private createAndAddWinField(): void {
        this.winField = new WinField();
        this.winField.position = this.WIN_FIELD_POSITION;
        this.addChild(this.winField);
    }

    private createAndAddToolBar(): void {
        this.toolbarMenu = new ToolBarMenu();
        this.toolbarMenu.position = this.TOOLBAR_POSITION;
        this.addChild(this.toolbarMenu);
    }

    private createAndAddDealButton(): void {
        this.dealButton = new Button(data.images.dealButtonBase, data.images.dealButtonOnHover,
            data.images.dealButtonOnClick, data.images.dealButtonInActive, data.audios.dealOrDrawButtonClicked);
        this.dealButton.position = this.CENTRAL_BUTTON_POSITION;
        this.addChild(this.dealButton);
    }

    private createAndAddDrawButton(): void {
        this.drawButton = new Button(data.images.drawBase, data.images.drawHover,
            data.images.drawClick, data.images.drawInActive, data.audios.dealOrDrawButtonClicked);
        this.drawButton.position = this.CENTRAL_BUTTON_POSITION;
        this.addChild(this.drawButton);
    }

    private createAndAddCollectButton(): void {
        this.collectButton = new Button(data.images.collectBase, data.images.collectHover,
            data.images.collectClick, data.images.collectInActive, data.audios.collectButtonClicked);
        this.collectButton.position = this.CENTRAL_BUTTON_POSITION;
        this.addChild(this.collectButton);
    }

    private createAndAddMaxBetButton(): void {
        this.maxBetButton = new Button(data.images.maxBetBase, data.images.maxBetHover, data.images.maxBetOnClick,
            data.images.maxBetInactive, data.audios.buttonClicked);
        this.maxBetButton.position = this.MAX_BET_BUTTON_POSITION;
        this.maxBetButton.subscribeOnClicked(() => {
            this.betPanel.setMaxBetValue();
        });
        this.addChild(this.maxBetButton);
    }

    private createAndAddDoubleUpButton(): void {
        this.doubleUpButton = new Button(data.images.doubleUpButtonBase, data.images.doubleUpButtonHover,
            data.images.doubleUpButtonOnClick, data.images.doubleUpButtonInactive, data.audios.buttonClicked);
        this.doubleUpButton.position = this.DOUBLE_UP_BUTTON_POSITION;
        this.addChild(this.doubleUpButton);
    }

    private createAndAddSpeedCheckbox(): void {
        this.speedCheckbox = new CheckBox(data.images.speedOffBase, data.images.speedOffHover,
            data.images.speedOffClick, data.images.speedOffInActive, data.images.speedOnBase, data.images.speedOnHover,
            data.images.speedOnClick, data.images.speedOnInActive, data.audios.buttonClicked);
        this.speedCheckbox.position = this.SPEED_CHECKBOX_POSITION;
        this.addChild(this.speedCheckbox);
    }

    private createAndAddCurrencySwitcher(): void {
        this.currencySwitcher = new CurrencySwitcher();
        this.currencySwitcher.position = this.CURRENCY_SWITCHER_POSITION;
        this.addChild(this.currencySwitcher);
    }

    private createAndAddBetPanel(): void {
        this.betPanel = new BetPanel();
        this.betPanel.position = this.BET_PANEL_POSITION;
        this.addChild(this.betPanel);
    }

    private createAndAddBackground(): void {
        const panelBackground = PIXI.Sprite.fromImage(data.images.betPanelBackground);
        this.addChild(panelBackground);
    }

    private createAndAddBalancePanel(): void {
        this.balancePanel = new BalancePanel();
        this.balancePanel.position = this.BALANCE_PANEL_POSITION;
        this.addChild(this.balancePanel);
        this.currencySwitcher.subscribeOnValueChanged((currencyType: string) => {
            this.balancePanel.displayCurrencyValue(currencyType);
        });
    }
}



// WEBPACK FOOTER //
// ./src/app/userInterface/GamePanel.ts